import { faArrowUpRightFromSquare, faCopy, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Collapse,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import get from 'lodash-es/get';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { truncatedTextStyle } from '../../constants/contractConfigForms';
import { EDIT_AD_GROUP_URL, EDIT_LINE_ITEM_URL, NEW_AD_GROUP_URL } from '../../constants/routeUrls';
import useAdGroups from '../../hooks/useAdGroups';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import useModal from '../../hooks/useModal';
import usePrevious from '../../hooks/usePrevious';
import { activeBackgroundColor, hoverBackgroundColor } from '../../styles/styles';
import mapLineItemToForm from '../../util/mapLineItemToForm';
import DuplicateLineItemForm from '../LineItems/DuplicateLineItemForm';
import AdGroupNav from './AdGroupNav';

interface LineItemNavProps {
  lineItem: any;
  filter?: string;
  onClick: () => any;
  expand?: boolean;
}

function LineItemNav({ lineItem, filter, onClick, expand }: LineItemNavProps) {
  const { id, name } = lineItem;
  const contractConfiguration = useContractConfigurationContext();
  const hasUnpausedCampaigns = get(contractConfiguration, 'hasUnpausedCampaigns');
  const contractId = get(contractConfiguration, 'contractData.id');
  const [isHovered, setHovered] = useState(false);

  const previousHasUnpausedCampaigns = usePrevious(hasUnpausedCampaigns);

  const [isRefetching, setIsRefetching] = React.useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const { isSuccess, isError, refetch, data } = useAdGroups(id);

  useEffect(() => {
    if (hasUnpausedCampaigns !== previousHasUnpausedCampaigns) {
      setIsRefetching(true);
      // There is some latency in updating the pause status
      setTimeout(() => {
        refetch();
        // Set a delay because we just called refetch above
        // and don't know if the status will be updated for all the adgroups.
        setTimeout(() => setIsRefetching(false), 1000);
      }, 1000);
    }
  }, [hasUnpausedCampaigns, previousHasUnpausedCampaigns, refetch]);

  const filterLowerCase = filter?.toLowerCase();

  const results = data?.collection.filter(
    (item) =>
      !filter || (filter && item.id.toString().includes(filter)) || item.name.toLowerCase().includes(filterLowerCase)
  );

  const [open, setOpen] = React.useState(false);
  useEffect(() => {
    if (results?.length <= 5) {
      setOpen(true);
    }
  }, [results?.length]);

  useEffect(() => {
    if (expand !== undefined && expand !== null) {
      setOpen(expand);
    }
  }, [expand]);

  const [showDuplicateLineItemModal, hideDuplicateLineItemModal] = useModal(
    () => <DuplicateLineItemForm lineItem={mapLineItemToForm(lineItem)} onClose={hideDuplicateLineItemModal} />,
    [lineItem]
  );

  const openLineItemInNewTab = () =>
    window.open(generatePath(EDIT_LINE_ITEM_URL, { id: contractId.toString(), lineItemId: id.toString() }));

  return (
    <>
      {((filter && (id.toString().includes(filter) || name.includes(filterLowerCase))) || !filter) && (
        <Tooltip title={`${name} (${id})`} placement="right">
          <ListItem
            divider
            sx={{
              backgroundColor: params.lineItemId === id?.toString() ? activeBackgroundColor : 'transparent',
              '&:hover': {
                backgroundColor: params.lineItemId === id?.toString() ? activeBackgroundColor : hoverBackgroundColor,
              },
              cursor: 'pointer',
            }}
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
          >
            <ListItemIcon>
              <IconButton onClick={() => setOpen(!open)}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography fontWeight="600" className="side-bar-item-title">
                  {name} ({id})
                </Typography>
              }
              primaryTypographyProps={{
                sx: truncatedTextStyle,
              }}
              onClick={onClick}
              sx={{ flexBasis: 0 }}
            />
            <Stack
              direction="row"
              spacing={1}
              ml={1}
              sx={{
                display: isHovered ? 'inherit' : 'none',
              }}
            >
              <Tooltip title="Add Ad Group" placement="top">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(
                      generatePath(NEW_AD_GROUP_URL, {
                        id: params.id,
                        lineItemId: id.toString(),
                      })
                    )
                  }
                  color="secondary"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Duplicate Line Item" placement="top">
                <IconButton size="small" onClick={showDuplicateLineItemModal}>
                  <FontAwesomeIcon icon={faCopy} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab" placement="top">
                <IconButton size="small" onClick={openLineItemInNewTab}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </IconButton>
              </Tooltip>
            </Stack>
          </ListItem>
        </Tooltip>
      )}
      {isSuccess && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {results.map((adGroup, index) => (
            <AdGroupNav
              key={adGroup.id || `ad-group-${index}`}
              adGroup={adGroup}
              index={index}
              count={results.length}
              isRefetching={isRefetching}
              onClick={() =>
                navigate(
                  `${generatePath(EDIT_AD_GROUP_URL, {
                    id: params.id,
                    adGroupId: adGroup.id,
                  })}${window.location.hash}`
                )
              }
            />
          ))}
        </Collapse>
      )}
      {isError && <Alert severity="error">Failed to load Ad Groups</Alert>}
    </>
  );
}

export default LineItemNav;
